import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'

import { ApolloQueryResult } from '@apollo/client/core/types'
import { Box } from '@factorialco/deprecated-design-system'
import { CurrencySelectorCurrencyFragment, GetExpensablesQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'

import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import Files from 'modules/expenses/components/expenses/Detail/_newForm/Files'
import AdditionalInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/AdditionalInformation'
import BudgetAndProject from 'modules/expenses/components/expenses/Detail/_newForm/Sections/BudgetAndProject'
import CostCenterInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/CostCenters'
import DocumentInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/DocumentInformation'
import ExpenseInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/ExpenseInformation'
import PaymentInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/PaymentInformation'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { isAutomaticExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

interface ExpenseFormProps {
  customizationFields: CustomizationField[]
  canSeeEmployeeLegalEntity: boolean
  canCustomizeSettings: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  canCreateExpensesForOthers: boolean
  canEditExpensesWithPendingStatus: boolean
  expensable?: Expensable
  refetchExpensable?: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
  hasProjectsFeature: boolean
  hasReportOnBehalfFeature: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  projectWorkers: ProjectWorker[]
  budgetOptions: BudgetOption[]
  loadingProjectWorkers: boolean
  loadingBudgetOptions: boolean
  onDocumentTypeChange: (value: string) => void
  onEmployeeIdChange: (employeeId: number | undefined) => void
  onEffectiveOnChange: (value: string) => void
  scrollTo?: string | undefined
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
}

const NewExpenseForm = (props: ExpenseFormProps) => {
  const {
    currencies,
    hasProjectsFeature,
    expensable,
    refetchExpensable,
    canCustomizeSettings,
    taxAgencyOrganization,
    projectWorkers,
    budgetOptions,
    loadingProjectWorkers,
    loadingBudgetOptions,
    onDocumentTypeChange,
    onEmployeeIdChange,
    onEffectiveOnChange,
    scrollTo,
    registerDropHandler,
    canEditExpensesWithPendingStatus,
  } = props
  const { section } = useOutletContext<RouteContextParams>()
  const asReviewer = !isMyExpensesSection(section)

  const documentInformationRef = useRef<HTMLDivElement>(null)
  const paymentInformationRef = useRef<HTMLDivElement>(null)
  const basicInformationRef = useRef<HTMLDivElement>(null)
  const budgetAndProjectRef = useRef<HTMLDivElement>(null)
  const additionalInformationRef = useRef<HTMLDivElement>(null)
  const costCenterInformationRef = useRef<HTMLDivElement>(null)

  const { employee } = useCurrentSession()
  const { watch } = useFormContext()

  useEffect(() => {
    // The timeout gives a more graceful animation experience
    setTimeout(() => {
      let ref: HTMLDivElement | null = null
      switch (scrollTo) {
        case 'basicInformation':
          ref = basicInformationRef.current
          break
        case 'documentInformation':
          ref = documentInformationRef.current
          break
        case 'paymentInformation':
          ref = paymentInformationRef.current
          break
        case 'budgetAndProject':
          ref = budgetAndProjectRef.current
          break
        case 'costCenterInformation':
          ref = costCenterInformationRef.current
          break
        case 'additionalInformation':
          ref = additionalInformationRef.current
          break
      }
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 300)
  }, [scrollTo])

  const documentType = watch('documentType')

  const isAutomatic = expensable ? isAutomaticExpensable(expensable) : false

  return (
    <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
      <BasicDetails
        expensable={expensable}
        canEditExpensesWithPendingStatus={canEditExpensesWithPendingStatus}
      />

      <Box ref={basicInformationRef}>
        <ExpenseInformation
          {...props}
          currentEmployee={employee}
          resourceType={'expense'}
          asReviewer={asReviewer}
          onEmployeeIdChange={onEmployeeIdChange}
          isAutomaticExpensable={isAutomatic}
          documentType={documentType}
        />
      </Box>
      <Box ref={documentInformationRef}>
        <DocumentInformation
          {...props}
          currentEmployee={employee}
          currencies={currencies}
          resourceType={'expense'}
          asReviewer={asReviewer}
          isAutomaticExpensable={isAutomatic}
          documentType={documentType}
          onDocumentTypeChange={onDocumentTypeChange}
          onEffectiveOnChange={onEffectiveOnChange}
        />
      </Box>
      <Box ref={paymentInformationRef}>
        <PaymentInformation
          {...props}
          currentEmployee={employee}
          resourceType={'expense'}
          asReviewer={asReviewer}
          documentType={documentType}
          canCustomizeSettings={canCustomizeSettings}
          isAutomaticExpensable={isAutomatic}
        />
      </Box>
      <Box ref={budgetAndProjectRef}>
        <BudgetAndProject
          {...props}
          currentEmployee={employee}
          resourceType={'expense'}
          asReviewer={asReviewer}
          documentType={documentType}
          expensableBudget={expensable?.bugdetMovement?.budget}
          budgetOptions={budgetOptions}
          projectWorkers={projectWorkers}
          hasProjectsFeature={hasProjectsFeature}
          loadingProjectWorkers={loadingProjectWorkers}
          loadingBudgetOptions={loadingBudgetOptions}
        />
      </Box>
      <Box ref={costCenterInformationRef}>
        <CostCenterInformation
          {...props}
          currentEmployee={employee}
          resourceId={expensable?.id}
          asReviewer={asReviewer}
          resourceType={'expense'}
          documentType={documentType}
        />
      </Box>
      <Box ref={additionalInformationRef}>
        <AdditionalInformation
          {...props}
          currentEmployee={employee}
          resourceType={'expense'}
          asReviewer={asReviewer}
          documentType={documentType}
          expensable={expensable}
        />
      </Box>
      <Files
        {...props}
        currentEmployee={employee}
        fieldIdentifier="files"
        resourceType={'expense'}
        asReviewer={asReviewer}
        documentType={documentType}
        refetchExpensable={refetchExpensable}
        taxAgencyOrganization={taxAgencyOrganization}
        registerDropHandler={registerDropHandler}
      />
    </Box>
  )
}

export default NewExpenseForm
